import React, { useId } from 'react';
import type { DischargeNotice } from '../../../dtos/patientmr/dischargeSummary.dto';
import {
  NOTICE_EXCEPTION_OPTIONS,
  NOTICE_METHOD_OPTIONS,
  NOTICE_RECIPIENT_OPTIONS,
  PHYSICIAN_NOTICE_OPTIONS,
  noticePeriodMet,
} from '../../../utils/dischargeNotice';

export interface DischargeNoticeFieldsProps {
  value: Partial<DischargeNotice>;
  onChange: (next: Partial<DischargeNotice>) => void;
  dischargeDate: string;
  /** Field names to flag after a submit attempt */
  missing: string[];
  disabled?: boolean;
}

const inputClass = 'mt-1 w-full rounded-md border border-slate-300 bg-white p-2 text-sm';
const labelClass = 'block text-sm font-medium text-slate-700';

/**
 * 26 TAC §558.295 discharge notice record (Phase 16E, DSC-19): written notice to the patient or
 * legal representative 5 days ahead (8 working days if mailed, plus personal contact), the date the
 * physician was told, and an exception when the notice period is not met.
 */
const DischargeNoticeFields: React.FC<DischargeNoticeFieldsProps> = ({ value, onChange, dischargeDate, missing, disabled }) => {
  const id = useId();
  const set = (patch: Partial<DischargeNotice>) => onChange({ ...value, ...patch });
  const flag = (field: string) => missing.includes(field);
  const periodMet = noticePeriodMet(value, dischargeDate);
  const isMail = value.noticeMethod === 'MAIL';

  return (
    <fieldset className="mt-4 rounded-md border border-slate-200 p-3" disabled={disabled}>
      <legend className="px-1 text-sm font-semibold text-slate-800">Discharge notice</legend>
      <p className="text-xs text-slate-600">
        Texas requires written notice at least 5 days before discharge (8 working days if mailed, with a phone call or visit
        at least 5 days before), and the physician must be told the discharge date.
      </p>

      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor={`${id}-date`} className={labelClass}>
            Notice given on <span className="text-red-600" aria-hidden="true">*</span>
          </label>
          <input
            id={`${id}-date`}
            type="date"
            value={value.noticeDate ?? ''}
            max={dischargeDate || undefined}
            onChange={(e) => set({ noticeDate: e.target.value })}
            aria-invalid={flag('noticeDate')}
            className={inputClass}
          />
          {flag('noticeDate') && <p className="mt-1 text-xs text-red-700">Enter the notice date (on or before the discharge date).</p>}
        </div>
        <div>
          <label htmlFor={`${id}-method`} className={labelClass}>
            How <span className="text-red-600" aria-hidden="true">*</span>
          </label>
          <select
            id={`${id}-method`}
            value={value.noticeMethod ?? ''}
            onChange={(e) => set({ noticeMethod: (e.target.value || undefined) as DischargeNotice['noticeMethod'] })}
            aria-invalid={flag('noticeMethod')}
            className={inputClass}
          >
            <option value="">Select…</option>
            {NOTICE_METHOD_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          {flag('noticeMethod') && <p className="mt-1 text-xs text-red-700">Choose how the notice was given.</p>}
        </div>
        <div>
          <label htmlFor={`${id}-recipient`} className={labelClass}>
            Given to <span className="text-red-600" aria-hidden="true">*</span>
          </label>
          <select
            id={`${id}-recipient`}
            value={value.noticeRecipientType ?? ''}
            onChange={(e) => set({ noticeRecipientType: (e.target.value || undefined) as DischargeNotice['noticeRecipientType'] })}
            aria-invalid={flag('noticeRecipientType')}
            className={inputClass}
          >
            <option value="">Select…</option>
            {NOTICE_RECIPIENT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          {flag('noticeRecipientType') && <p className="mt-1 text-xs text-red-700">Choose who received the notice.</p>}
        </div>
        <div>
          <label htmlFor={`${id}-name`} className={labelClass}>Recipient name</label>
          <input
            id={`${id}-name`}
            type="text"
            maxLength={120}
            value={value.noticeRecipientName ?? ''}
            onChange={(e) => set({ noticeRecipientName: e.target.value })}
            className={inputClass}
          />
        </div>
        {isMail && (
          <div>
            <label htmlFor={`${id}-contact`} className={labelClass}>Phone or in-person contact on</label>
            <input
              id={`${id}-contact`}
              type="date"
              value={value.personalContactDate ?? ''}
              max={dischargeDate || undefined}
              onChange={(e) => set({ personalContactDate: e.target.value })}
              className={inputClass}
            />
          </div>
        )}
        <div>
          <label htmlFor={`${id}-md-date`} className={labelClass}>
            Physician told on <span className="text-red-600" aria-hidden="true">*</span>
          </label>
          <input
            id={`${id}-md-date`}
            type="date"
            value={value.physicianNotifiedDate ?? ''}
            onChange={(e) => set({ physicianNotifiedDate: e.target.value })}
            aria-invalid={flag('physicianNotifiedDate')}
            className={inputClass}
          />
          {flag('physicianNotifiedDate') && <p className="mt-1 text-xs text-red-700">Enter when the physician was told.</p>}
        </div>
        <div>
          <label htmlFor={`${id}-md-method`} className={labelClass}>
            Physician told by <span className="text-red-600" aria-hidden="true">*</span>
          </label>
          <select
            id={`${id}-md-method`}
            value={value.physicianNotifiedMethod ?? ''}
            onChange={(e) => set({ physicianNotifiedMethod: (e.target.value || undefined) as DischargeNotice['physicianNotifiedMethod'] })}
            aria-invalid={flag('physicianNotifiedMethod')}
            className={inputClass}
          >
            <option value="">Select…</option>
            {PHYSICIAN_NOTICE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          {flag('physicianNotifiedMethod') && <p className="mt-1 text-xs text-red-700">Choose how the physician was told.</p>}
        </div>
      </div>

      {periodMet === true && (
        <p className="mt-3 text-xs text-emerald-800" role="status" data-testid="notice-period-met">Notice period met.</p>
      )}
      {periodMet === false && (
        <div className="mt-3" data-testid="notice-period-short">
          <p className="text-xs text-amber-800" role="status">
            {isMail
              ? 'A mailed notice needs 8 working days and a phone call or visit at least 5 days before discharge.'
              : 'Less than 5 days of notice.'}{' '}
            Choose the exception that applies.
          </p>
          <label htmlFor={`${id}-exception`} className={`${labelClass} mt-2`}>
            Exception <span className="text-red-600" aria-hidden="true">*</span>
          </label>
          <select
            id={`${id}-exception`}
            value={value.noticeException ?? ''}
            onChange={(e) => set({ noticeException: (e.target.value || null) as DischargeNotice['noticeException'] })}
            aria-invalid={flag('noticeException')}
            className={inputClass}
          >
            <option value="">Select…</option>
            {NOTICE_EXCEPTION_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          {flag('noticeException') && <p className="mt-1 text-xs text-red-700">Choose an exception or change the dates.</p>}
        </div>
      )}
    </fieldset>
  );
};

export default DischargeNoticeFields;
